import type { DocId, MemberRolesMap, Timestamp, UserId } from './common.js';

/**
 * Row kind in the WBS tree.
 *
 *   phase     — top-level bucket (e.g. "移行テスト④"). Typically the row
 *               that would link to a migration `Run` via `linkedRunId`.
 *   task      — regular WBS row. May nest under another task via
 *               `parentItemId`.
 *   milestone — single-date marker (release date, deliverable, etc.).
 *               Stored as a row but rendered in the Gantt as a vertical
 *               line + ▼ instead of a duration bar. Convention: write
 *               the same Timestamp into both `plannedStart` and
 *               `plannedEnd` so range-based math (axis range, sort) is
 *               unaffected; UI hides the end column for these rows.
 *
 * Phases are always top-level (`parentItemId === null` implies either a
 * phase or a top-level task/milestone; use `kind` to disambiguate).
 */
export type WbsItemKind = 'phase' | 'task' | 'milestone';

/**
 * Progress/execution state for a WBS row. Distinct from DAG
 * `ExecutionStatus`: WBS status covers the full PM surface (including
 * blocked / cancelled rows that never map to a migration execution).
 */
export type WbsItemStatus =
  | 'not_started'
  | 'in_progress'
  | 'done'
  | 'blocked'
  | 'cancelled';

/**
 * Controls whether `progress` on a parent is owned by the client (manual)
 * or rolled up from children by the `aggregateWbsParent` Cloud Function
 * (auto). Leaf rows always behave as `manual` — the aggregator ignores
 * rows without children.
 *
 * The `progressMode === 'auto'` contract is asymmetric:
 *   - The aggregator may write `plannedStart` / `plannedEnd` / `progress`
 *     on the parent regardless of mode (period rollup is always auto).
 *   - Only `progress` honors the `manual` escape hatch — clients that
 *     want to pin a value flip the mode to `manual` and write the value.
 *
 * See `design-wbs-gantt.md` for the full aggregation contract.
 */
export type WbsProgressMode = 'auto' | 'manual';

/**
 * `/projects/{projectId}/wbsItems/{itemId}` — single row in the project's
 * WBS / Gantt. Phases and tasks share this shape; `kind` discriminates.
 *
 * Scope: this collection holds the **project management view** (education,
 * documentation, vendor coordination, etc.). Migration execution lives in
 * `/tasks` + `/executions` and is linked from here via `linkedRunId` /
 * `linkedMigrationTaskIds` — never mirrored.
 */
export interface WbsItemDoc {
  itemId: DocId;
  projectId: string;

  kind: WbsItemKind;
  /**
   * Tree parent within `wbsItems`. `null` for top-level rows (phases and
   * top-level tasks). A task row's `parentItemId` may point to either a
   * phase or another task.
   */
  parentItemId: DocId | null;
  /**
   * Display-order key. Source-format examples: `"P0"` (phase), `"1-5"`
   * (task under phase P1), `"1-5-3"` (sub-task under `"1-5"`). Stored as
   * the raw string so the UI can sort lexicographically with a
   * segment-aware comparator (see design doc).
   */
  sortKey: string;

  name: string;
  /**
   * Combined long-form notes / description. Historical schemas had a
   * separate `notes` field; the `migrate:merge-wbs-note-into-description`
   * migration folded them in here so the detail panel only surfaces a
   * single multi-line block.
   */
  description: string;

  /**
   * Multi-line "完了基準" set on `kind === 'phase'` rows. Task / milestone
   * rows ignore this field — the migration-task views read the parent
   * phase's value and present it as a shared criterion. Stored as
   * plain text; newlines are preserved by the UI (whitespace-pre-wrap)
   * but no Markdown is rendered. Capped at 5000 characters by the
   * client; absent / null on rows without a criterion configured.
   */
  completionCriteria?: string | null;

  /**
   * 領域 labels for this row (multi-value). Each entry is a
   * `WbsCatalogOption.label` from the project's `wbsCatalog.area`. An
   * empty array means "未設定". This is the canonical field — all new
   * code reads and writes it. Mirrors Backlog's multi-category model
   * (one issue / row can belong to several 領域).
   */
  systems: string[];
  /**
   * @deprecated Legacy single-value 領域 column. Kept only as a
   * dual-read fallback for docs not yet migrated to {@link systems};
   * the `migrate:wbs-area-array` script deletes it. Never write this
   * from new code — read 領域 via {@link readWbsItemSystems}.
   */
  system?: string;
  /** Source column "vendor" (e.g. "横河SS", "自社"). */
  vendor: string;
  /**
   * Reference into `/projects/{projectId}/assignees`. The single
   * canonical link from a WBS row to its assignee. `null` means the
   * row is unassigned. Resolve the display name and color through
   * the matching `AssigneeDoc`.
   */
  assigneeId: DocId | null;

  /** Planned start/end as calendar-day Timestamps at 00:00 JST. */
  plannedStart: Timestamp | null;
  plannedEnd: Timestamp | null;
  /** Actuals. Null until the row has started / finished. */
  actualStart: Timestamp | null;
  actualEnd: Timestamp | null;

  /**
   * Duration in days as authored in the source sheet. Used as the
   * weighting factor in parent-progress rollup (duration-weighted mean of
   * children).
   */
  durationDays: number;

  /** Completion percentage 0..100. */
  progress: number;
  progressMode: WbsProgressMode;

  status: WbsItemStatus;

  /**
   * Predecessor `itemId` list (source column "predecessors"). Cycle
   * detection is NOT enforced here by rules; the `validateWbsCycles`
   * Cloud Function owns that contract (writes `cycleDetected` /
   * `cycleMembers`).
   */
  dependsOn: DocId[];

  /**
   * Set by the `validateWbsCycles` onWrite trigger when the row
   * participates in a `dependsOn` cycle. Optional for forward compatibility
   * with docs written before the field existed — the UI should treat a
   * missing value as `false`.
   */
  cycleDetected?: boolean;
  /**
   * When `cycleDetected` is true, the sorted list of `itemId`s that
   * comprise the cycle (so the UI can highlight them together). Empty or
   * absent when the row is acyclic.
   */
  cycleMembers?: DocId[];

  /**
   * Backlog issue keys this row tracks. Normalized from the source
   * `backlog_url` column plus `_ext_issues`. Registered with
   * `backlogIssueIndex` by the `maintainBacklogIndexFromWbs` trigger so
   * Backlog sync picks them up. Multiple keys are aggregated — see the
   * roll-up contract on `backlogAutoUpdatedAt`.
   */
  backlogIssueKeys: string[];
  /**
   * Marker for rows that were auto-imported by the phase-level
   * Backlog-milestone link (see {@link PhaseBacklogMilestoneLink}). Set
   * only on `kind === 'task'` rows that the link initially created.
   *
   * Kept after the user clears `backlogIssueKeys` (or after the issue
   * leaves the milestone) so the UI can still badge the row as
   * "originally imported from milestone X" and the unlink callable can
   * find every imported row in O(1) per row.
   *
   * Optional / null for any row not produced by the link.
   */
  backlogMilestoneOrigin?: BacklogMilestoneOrigin | null;
  /**
   * Denormalized cache of the Backlog **milestone** names the linked
   * issue currently belongs to, copied from Backlog on every sync. Set
   * only on rows that mirror exactly one Backlog issue (the milestone
   * model is per-issue). Read-only on the WBS side — the UI surfaces it
   * as "マイルストン: 移行テスト⑤" and the auto-relinker
   * ({@link backlogMilestoneMismatch}) keys off it.
   *
   * `null` / absent for manual rows or rows that mirror an issue with no
   * milestone. An empty array means "synced, but the issue has no
   * milestone" (distinct from never-synced `null`).
   */
  backlogSourceMilestoneNames?: readonly string[] | null;
  /**
   * Denormalized cache of the Backlog **category** names the linked
   * issue currently carries (e.g. APG「MDM」「POWER BI」). Same contract
   * as {@link backlogSourceMilestoneNames} but for the category-based
   * model. Surfaced read-only as "カテゴリ: MDM, POWER BI".
   */
  backlogSourceCategoryNames?: readonly string[] | null;
  /**
   * Set by the auto-relinker (`core/backlog/relinker.ts`) when a
   * Backlog-linked row's source milestone (or category) has no matching
   * phase in the same project, so the row could not be moved to the
   * right place automatically. The detail panel surfaces a prominent
   * "⚠ 連携先マイルストン不整合発生中" banner while this is set.
   *
   * Cleared automatically on the next sync once a matching phase is
   * found (or the row is moved). `null` / absent in the normal case.
   */
  backlogMilestoneMismatch?: BacklogMilestoneMismatch | null;
  /**
   * Last full-orphan signature the sync notifier already announced for
   * this row.
   *
   * "Orphan" = the issue has NO anchor path to any phase in the project
   * (its milestone, category, AND parent-issue all miss). Computed as a
   * stable hash of the full anchor state via `buildOrphanSignature` so
   * any axis moving — milestone shifted, category added, parent-issue
   * swapped — flips the signature and re-opens the Discord channel.
   *
   * Cleared back to `null` once any axis re-anchors the issue (the
   * relinker stops flagging the row as orphan), so the next distinct
   * orphan state fires.
   *
   * Optional / null in the normal case; absent on rows written before
   * the field landed.
   */
  lastNotifiedOrphanSignature?: string | null;
  /**
   * @deprecated Per-axis dedup key from the original orphan-notify
   * implementation. Superseded by {@link lastNotifiedOrphanSignature}
   * which keys on the FULL anchor state (milestone + category + parent
   * issue) so a category-anchored row isn't false-positive flagged when
   * only its milestone shifted. Still read as a one-shot fallback for
   * rows written before the rewrite; the relinker clears it on the
   * next mismatch / 'none' tick. No data migration needed — null reads
   * as "nothing to announce".
   */
  lastNotifiedOrphanMilestoneId?: string | null;
  /**
   * Phase-level links to Backlog milestones (Backlog "versions"). Set
   * only on `kind === 'phase'` rows; absent / empty otherwise.
   *
   * One phase can link N milestones, including milestones from
   * different Backlog projects under the same workspace — useful when a
   * single migration phase tracks parallel work streams that the
   * Backlog side decomposed into separate projects. Entries are
   * deduplicated by `(backlogProjectKey, milestoneId)`. The link list
   * drives the `syncAllBacklogMilestones` cron and the on-demand
   * `syncBacklogMilestoneNow` callable — both pull each milestone's
   * latest issue list from Backlog and reconcile against the phase's
   * existing child rows.
   */
  backlogMilestoneLinks?: PhaseBacklogMilestoneLink[];
  /**
   * Marker for rows that were auto-imported by a phase-level
   * Backlog-category link (see {@link PhaseBacklogCategoryLink}). The
   * category analogue of {@link backlogMilestoneOrigin}: set only on
   * `kind === 'task'` rows that a category link initially created,
   * carrying the numeric `categoryId` so the unlink / cron paths can
   * find every imported row with a single indexed query. Optional / null
   * on any row not produced by a category link.
   */
  backlogCategoryOrigin?: BacklogCategoryOrigin | null;
  /**
   * Phase-level links to Backlog **categories** (カテゴリー). Set only on
   * `kind === 'phase'` rows; absent / empty otherwise.
   *
   * The category analogue of {@link backlogMilestoneLinks}: a phase can
   * link N categories, including categories from different Backlog
   * projects under the same workspace. Each entry mirrors that
   * category's issues (`/api/v2/issues?categoryId[]=`) as direct child
   * WBS tasks under the phase. Entries are deduplicated by
   * `(backlogProjectKey, categoryId)`. Drives the
   * `syncAllBacklogCategories` cron and the on-demand
   * `syncBacklogCategoryNow` callable. Useful for Backlog projects (e.g.
   * APG) organised by category rather than milestone.
   */
  backlogCategoryLinks?: PhaseBacklogCategoryLink[];
  /**
   * Marker for rows that were auto-imported by a phase-level
   * Backlog-parent-issue link (see {@link PhaseBacklogParentIssueLink}).
   * The parent-issue analogue of {@link backlogMilestoneOrigin}: set only
   * on `kind === 'task'` rows that a parent-issue link initially created,
   * carrying the numeric `parentIssueId` so the unlink / cron paths can
   * find every imported row with a single indexed query. Optional / null
   * on any row not produced by a parent-issue link.
   */
  backlogParentIssueOrigin?: BacklogParentIssueOrigin | null;
  /**
   * Phase-level links to Backlog **parent issues** (親課題). Set only on
   * `kind === 'phase'` rows; absent / empty otherwise.
   *
   * The parent-issue analogue of {@link backlogMilestoneLinks}: a phase
   * can link N parent issues (e.g. APG-5639「MDM開発・改修」), including
   * parent issues from different Backlog projects under the same
   * workspace. Each entry mirrors that parent's child issues
   * (`/api/v2/issues?parentIssueId[]=`) as direct child WBS tasks under
   * the phase. Entries are deduplicated by
   * `(backlogProjectKey, parentIssueId)`. Drives the
   * `syncAllBacklogParentIssues` cron and the on-demand
   * `syncBacklogParentIssueNow` callable.
   */
  backlogParentIssueLinks?: PhaseBacklogParentIssueLink[];
  /**
   * Last time the Backlog roll-up (see
   * `functions/src/core/wbs/backlogRollup.ts`) auto-wrote `status` or
   * `actualEnd` on this row. UI surfaces this as a small "Backlog 同期
   * x 分前" badge so the user can tell auto-filled values apart from
   * manual ones.
   *
   * Contract:
   *   - The web client clears this to `null` whenever a manual patch
   *     touches `status` or `actualEnd` — the user reclaims authorship.
   *   - The Cloud Function only writes here when it also wrote one of
   *     those fields. Fill-only: it never overwrites a non-default
   *     manual value (manual `done`, manual `actualEnd`).
   *
   * Optional for backward compatibility with rows written before the
   * field landed.
   */
  backlogAutoUpdatedAt?: Timestamp | null;

  /**
   * Set when this row is a 'shadow' / phantom mirror of another WbsItem
   * (the **original**) that lives in a DIFFERENT phase. The shadow exists
   * so children imported into THIS phase (by a milestone / category link)
   * can keep their Backlog parent-child relationship visible — without it
   * the child would float orphaned at the phase root.
   *
   * Contract:
   *   - Points to the original {@link WbsItemDoc.itemId}. The original is
   *     always a NON-shadow row (`shadowOf == null`); if a candidate
   *     parent is itself a shadow, the sync resolves through to the real
   *     original before linking.
   *   - The shadow mirrors a subset of the original's fields (name,
   *     status, assignee, planned dates, progress) on each sync; it is
   *     never edited directly and carries NO `backlogIssueKeys` (so it is
   *     skipped by the Backlog index / per-issue rollup / relinker) and NO
   *     `backlog*Origin` (so reconcile never counts it as an imported
   *     issue).
   *   - Garbage-collected automatically once no row references it as a
   *     `parentItemId` (see `cleanupEmptyShadows`).
   *   - The progress aggregator skips shadow rows so the mirror is not
   *     clobbered by a roll-up over the partial child set in this phase.
   *   - `null` / absent for normal rows. Use {@link isWbsItemShadow}.
   */
  shadowOf?: string | null;

  /** Denormalized from parent project for Security Rules (see addendum §2). */
  memberUserIds: UserId[];
  memberRoles: MemberRolesMap;

  createdAt: Timestamp;
  updatedAt: Timestamp;
  /**
   * Source-of-record `last_modified` (Google Sheets timestamp in the seed
   * file). Used to decide whether a seed rerun should overwrite this row.
   */
  lastModifiedAt: Timestamp;
}

/**
 * Phase ↔ Backlog milestone link. Lives on a phase
 * (`WbsItemDoc` with `kind === 'phase'`) inside the
 * `backlogMilestoneLinks` array. A phase can hold multiple entries
 * (one per linked milestone); entries are unique by
 * `(backlogProjectKey, milestoneId)`.
 *
 * Each entry tells the milestone-sync surface (callable + cron) which
 * milestone to mirror and bookkeeps that entry's last sync attempt
 * so the UI can surface "最終同期 x 分前" / errors without a separate
 * doc.
 */
export interface PhaseBacklogMilestoneLink {
  /** Backlog project key (e.g. "MIG"). Must be one of the project's `backlog.backlogProjectKeys`. */
  backlogProjectKey: string;
  /** Display name resolved at link time — denormalized for the UI. */
  backlogProjectName: string;
  /** Backlog milestone (version) id. */
  milestoneId: number;
  /** Milestone display name resolved at link time. */
  milestoneName: string;
  /** When the link was first established. */
  linkedAt: Timestamp;
  /** UID of the user who established the link (audit / activity). */
  linkedByUserId: UserId;
  /** Last successful sync timestamp. `null` until the first sync completes. */
  lastSyncedAt?: Timestamp | null;
  /** Most recent sync error message (truncated). `null` when last sync succeeded. */
  lastSyncError?: string | null;
  /** Consecutive failures — input to exponential backoff. Reset on success. */
  consecutiveFailures?: number;
}

/**
 * Set on `kind === 'task'` rows that the phase-milestone link created.
 * `milestoneId` is denormalized so the unlink path can find every
 * imported row with a single `where('backlogMilestoneOrigin.milestoneId', '==', n)`
 * query — no scan across the whole project.
 */
export interface BacklogMilestoneOrigin {
  milestoneId: number;
  milestoneName: string;
  importedAt: Timestamp;
}

/**
Phase ↔ Backlog category link. Lives on a phase
 * (`WbsItemDoc` with `kind === 'phase'`) inside the
 * `backlogCategoryLinks` array. A phase can hold multiple entries
 * (one per linked category); entries are unique by
 * `(backlogProjectKey, categoryId)`.
 *
 * The category analogue of {@link PhaseBacklogMilestoneLink}: each entry
 * tells the category-sync surface (callable + cron) which Backlog
 * category's issues to mirror as direct child WBS tasks under the phase,
 * and bookkeeps that entry's last sync attempt so the UI can surface
 * "最終同期 x 分前" / errors without a separate doc.
 */
export interface PhaseBacklogCategoryLink {
  /** Backlog project key (e.g. "APG"). Must be one of the project's `backlog.backlogProjectKeys`. */
  backlogProjectKey: string;
  /** Display name resolved at link time — denormalized for the UI. */
  backlogProjectName: string;
  /** Backlog category id. */
  categoryId: number;
  /** Category display name resolved at link time. */
  categoryName: string;
  /** When the link was first established. */
  linkedAt: Timestamp;
  /** UID of the user who established the link (audit / activity). */
  linkedByUserId: UserId;
  /** Last successful sync timestamp. `null` until the first sync completes. */
  lastSyncedAt?: Timestamp | null;
  /** Most recent sync error message (truncated). `null` when last sync succeeded. */
  lastSyncError?: string | null;
  /** Consecutive failures — input to exponential backoff. Reset on success. */
  consecutiveFailures?: number;
}

/**
 * Set on `kind === 'task'` rows that a phase-category link created.
 * `categoryId` is denormalized so the unlink path can find every
 * imported row with a single
 * `where('backlogCategoryOrigin.categoryId', '==', n)` query — no scan
 * across the whole project.
 */
export interface BacklogCategoryOrigin {
  categoryId: number;
  categoryName: string;
  importedAt: Timestamp;
}

/**
 * Warning state written onto a Backlog-linked WBS row when its source
 * milestone (or category) name does not match any phase in the project,
 * so the auto-relinker could not move it to the correct phase. Surfaced
 * as a banner in the detail panel; cleared by the next sync that finds a
 * matching phase.
 */
export interface BacklogMilestoneMismatch {
  /**
   * The Backlog milestone (or category) names that had no matching
   * phase. Usually a single entry, but Backlog allows an issue to belong
   * to several milestones, so this is a list.
   */
  sourceMilestoneNames: string[];
  /** When the mismatch was last detected (server clock at sync time). */
  detectedAt: Timestamp;
}

/**
 * Phase ↔ Backlog parent-issue link. Lives on a phase
 * (`WbsItemDoc` with `kind === 'phase'`) inside the
 * `backlogParentIssueLinks` array. A phase can hold multiple entries
 * (one per linked parent issue); entries are unique by
 * `(backlogProjectKey, parentIssueId)`.
 *
 * Each entry tells the parent-issue-sync surface (callable + cron)
 * which Backlog parent issue's child issues to mirror as direct child
 * WBS tasks under the phase, and bookkeeps that entry's last sync
 * attempt so the UI can surface "最終同期 x 分前" / errors without a
 * separate doc.
 */
export interface PhaseBacklogParentIssueLink {
  /** Backlog project key (e.g. "APG"). Must be one of the project's `backlog.backlogProjectKeys`. */
  backlogProjectKey: string;
  /** Display name resolved at link time — denormalized for the UI. */
  backlogProjectName: string;
  /** Backlog numeric issue id of the parent (stable across renames / key changes). */
  parentIssueId: number;
  /** Parent issue key for display / external links (e.g. "APG-5639"). */
  parentIssueKey: string;
  /** Parent issue summary snapshot resolved at link time — denormalized for the UI. */
  parentIssueSummary: string;
  /**
   * Forced `systems` value applied to every imported child row, for
   * Backlog projects whose category → 領域 mapping does not apply (e.g.
   * APG, where categories are not Migratus 領域 labels). When non-empty
   * it OVERRIDES the usual `category → systems` mapping (the
   * `validAreaLabels` filter). An empty array / undefined means "use the
   * normal category → systems mapping".
   */
  forceSystems?: string[];
  /** When the link was first established. */
  linkedAt: Timestamp;
  /** UID of the user who established the link (audit / activity). */
  linkedByUserId: UserId;
  /** Last successful sync timestamp. `null` until the first sync completes. */
  lastSyncedAt?: Timestamp | null;
  /** Most recent sync error message (truncated). `null` when last sync succeeded. */
  lastSyncError?: string | null;
  /** Consecutive failures — input to exponential backoff. Reset on success. */
  consecutiveFailures?: number;
}

/**
 * Set on `kind === 'task'` rows that a phase parent-issue link created.
 * `parentIssueId` is denormalized so the unlink path can find every
 * imported row with a single
 * `where('backlogParentIssueOrigin.parentIssueId', '==', n)` query — no
 * scan across the whole project.
 */
export interface BacklogParentIssueOrigin {
  parentIssueId: number;
  parentIssueKey: string;
  parentIssueSummary: string;
  importedAt: Timestamp;
}

/**
 * Dual-read accessor for a WBS row's 領域 list.
 *
 * Prefers the canonical {@link WbsItemDoc.systems} array. Falls back to
 * interpreting the legacy {@link WbsItemDoc.system} string as a
 * single-element list only when `systems` is entirely absent (a doc
 * written before the multi-value migration). A doc that has been
 * migrated to an empty list (`systems: []`) correctly reads as empty
 * and does NOT resurrect the legacy value — `systems` being present
 * (even if `[]`) always wins.
 *
 * Pure; safe in render paths / `useMemo` deps.
 */
export function readWbsItemSystems(item: {
  systems?: readonly string[] | null;
  system?: string | null;
}): string[] {
  if (item.systems != null) return [...item.systems];
  const legacy = item.system;
  return legacy != null && legacy !== '' ? [legacy] : [];
}

/**
 * Fields on a WBS row that are mastered by Backlog once the row links one
 * or more Backlog issues. These mirror columns Backlog owns (summary →
 * name, description, status, start/due dates, assignee, category →
 * systems); edits to them are blocked in the WBS UI so Backlog stays the
 * single source of truth. Fields NOT listed here (vendor, completionCriteria,
 * dependsOn, parentItemId, progress, actuals beyond the rollup) have no
 * Backlog counterpart and remain freely editable.
 */
export const BACKLOG_SYNCED_WBS_FIELDS = [
  'name',
  'description',
  'status',
  'plannedStart',
  'plannedEnd',
  'assigneeId',
  'systems',
] as const;

export type BacklogSyncedWbsField = (typeof BACKLOG_SYNCED_WBS_FIELDS)[number];

/**
 * True when this WBS row mirrors at least one Backlog issue and is
 * therefore Backlog-mastered. The UI uses this to make the
 * {@link BACKLOG_SYNCED_WBS_FIELDS} read-only and to badge the row as
 * "🔗 Backlog 由来". Pure / cheap; safe in render paths.
 *
 * Note: a row that was milestone-imported but later dropped from the
 * milestone has its `backlogIssueKeys` cleared (see milestoneSync) and so
 * reads as NOT linked — it becomes editable again, which is intended
 * (the user reclaims ownership once Backlog stops tracking it).
 */
export function isWbsItemBacklogLinked(item: {
  backlogIssueKeys?: readonly string[] | null;
}): boolean {
  return (item.backlogIssueKeys?.length ?? 0) > 0;
}

/**
 * True when this row is a shadow / phantom mirror of another phase's
 * WbsItem (see {@link WbsItemDoc.shadowOf}). Shadows are read-only in the
 * UI — all Backlog-mastered AND user-editable fields are disabled, and a
 * "↗ 元タスクへ" link navigates to the original. Pure / cheap; safe in
 * render paths.
 */
export function isWbsItemShadow(item: {
  shadowOf?: string | null;
}): boolean {
  return item.shadowOf != null && item.shadowOf !== '';
}

/** First Backlog issue key on the row, for lock messages / external links. `null` when unlinked. */
export function primaryBacklogIssueKey(item: {
  backlogIssueKeys?: readonly string[] | null;
}): string | null {
  return item.backlogIssueKeys && item.backlogIssueKeys.length > 0
    ? item.backlogIssueKeys[0]!
    : null;
}

/**
 * Backlog project key derived from the row's primary issue key. Backlog
 * issue keys are always `{PROJECT_KEY}-{N}` — split on the LAST hyphen so
 * project keys that themselves contain a hyphen (e.g. `CIS_TTM` or a
 * future `FOO-BAR` key) round-trip correctly. `null` when the row has no
 * Backlog issues or the key has no hyphen.
 */
export function primaryBacklogProjectKey(item: {
  backlogIssueKeys?: readonly string[] | null;
}): string | null {
  const primary = primaryBacklogIssueKey(item);
  if (!primary) return null;
  const idx = primary.lastIndexOf('-');
  if (idx <= 0) return null;
  return primary.slice(0, idx);
}
